import {
	USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE,
	USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE,
	USER_SEGMENTATION_TEAM_TYPE_DESIGN,
	USER_SEGMENTATION_TEAM_TYPE_FINANCE,
	USER_SEGMENTATION_TEAM_TYPE_HR,
	USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT,
	USER_SEGMENTATION_TEAM_TYPE_LEGAL,
	USER_SEGMENTATION_TEAM_TYPE_MARKETING,
	USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
	USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL,
	USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT,
	USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT,
	USER_SEGMENTATION_TEAM_TYPE_SALES,
	USER_SEGMENTATION_TEAM_TYPE_SKIP,
	USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
} from '../../models/user-segmentation';

import type { UserSegmentationTeamTypes } from '../../models/user-segmentation';

export type JsmUseCaseTemplateCardKey =
	| 'analytics-service-management'
	| 'customer-service-management'
	| 'design-service-management'
	| 'development-request'
	| 'finance-service-management'
	| 'general-service-management'
	| 'hr-service-management'
	| 'incident-management'
	| 'legal-service-management'
	| 'marketing-service-management'
	| 'sales-service-management'
	| 'service-request-management';

export type JsmUseCaseTemplateOption = {
	readonly templateId: string;
	readonly cardKey: JsmUseCaseTemplateCardKey;
	readonly isRecommended?: boolean;
};

const customerServiceTemplate = (isRecommended = false): JsmUseCaseTemplateOption => ({
	templateId: 'jcs.customer-service-management',
	cardKey: 'customer-service-management',
	isRecommended,
});

const serviceRequestTemplate = (isRecommended = false): JsmUseCaseTemplateOption => ({
	templateId: 'jsm.it-service-management-basic',
	cardKey: 'service-request-management',
	isRecommended,
});

const generalServiceTemplate = (isRecommended = false): JsmUseCaseTemplateOption => ({
	templateId: 'jsm.general-service-management',
	cardKey: 'general-service-management',
	isRecommended,
});

const developmentRequestTemplate = (isRecommended = false): JsmUseCaseTemplateOption => ({
	templateId: 'jsm.development-requests',
	cardKey: 'development-request',
	isRecommended,
});

const incidentManagementTemplate = (isRecommended = false): JsmUseCaseTemplateOption => ({
	templateId: 'jsm.it-service-management-basic',
	cardKey: 'incident-management',
	isRecommended,
});

const hrServiceTemplate = (isRecommended = false): JsmUseCaseTemplateOption => ({
	templateId: 'jsm.hr-service-management',
	cardKey: 'hr-service-management',
	isRecommended,
});

const designServiceTemplate = (isRecommended = false): JsmUseCaseTemplateOption => ({
	templateId: 'jsm.design-service-management',
	cardKey: 'design-service-management',
	isRecommended,
});

const marketingServiceTemplate = (isRecommended = false): JsmUseCaseTemplateOption => ({
	templateId: 'jsm.marketing-service-management',
	cardKey: 'marketing-service-management',
	isRecommended,
});

const analyticsServiceTemplate = (isRecommended = false): JsmUseCaseTemplateOption => ({
	templateId: 'jsm.analytics-service-management',
	cardKey: 'analytics-service-management',
	isRecommended,
});

const salesServiceTemplate = (isRecommended = false): JsmUseCaseTemplateOption => ({
	templateId: 'jsm.sales-service-management',
	cardKey: 'sales-service-management',
	isRecommended,
});

const financeServiceTemplate = (isRecommended = false): JsmUseCaseTemplateOption => ({
	templateId: 'jsm.finance-service-management',
	cardKey: 'finance-service-management',
	isRecommended,
});

const legalServiceTemplate = (isRecommended = false): JsmUseCaseTemplateOption => ({
	templateId: 'jsm.legal-service-management',
	cardKey: 'legal-service-management',
	isRecommended,
});

export const JSM_USE_CASE_DEFAULT_TEAM_TYPE = USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL;
export const CSM_PROJECT_TEMPLATE_BLUEPRINT_KEY = 'com.atlassian.jcs:customer-service-management';

/**
 * Maps the internal JSM template IDs used in the template choice screen
 * to the Jira blueprint keys accepted by /rest/simplified/latest/project.
 *
 * These blueprint keys are registered in the SD plugin's
 * sd-project-blueprints-classic.xml and sd-project-blueprints-next-gen.xml.
 */
export const JSM_TEMPLATE_ID_TO_BLUEPRINT_KEY: Record<string, string> = {
	'jsm.it-service-management-basic':
		'com.atlassian.servicedesk:simplified-it-service-management-basic',
	'jsm.general-service-management':
		'com.atlassian.servicedesk:company-managed-general-service-project',
	'jcs.customer-service-management': CSM_PROJECT_TEMPLATE_BLUEPRINT_KEY,
	'jsm.hr-service-management': 'com.atlassian.servicedesk:simplified-hr-service-desk',
	'jsm.development-requests':
		'com.atlassian.servicedesk:simplified-development-requests-service-desk',
	'jsm.design-service-management': 'com.atlassian.servicedesk:simplified-design-service-desk',
	'jsm.marketing-service-management': 'com.atlassian.servicedesk:simplified-marketing-service-desk',
	'jsm.analytics-service-management': 'com.atlassian.servicedesk:simplified-analytics-service-desk',
	'jsm.sales-service-management': 'com.atlassian.servicedesk:simplified-external-service-desk',
	'jsm.finance-service-management': 'com.atlassian.servicedesk:simplified-finance-service-desk',
	'jsm.legal-service-management': 'com.atlassian.servicedesk:simplified-legal-service-desk',
};

export const JSM_USE_CASE_TEMPLATE_OPTIONS_BY_TEAM_TYPE: Record<
	UserSegmentationTeamTypes,
	readonly JsmUseCaseTemplateOption[]
> = {
	[USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT]: [
		customerServiceTemplate(),
		serviceRequestTemplate(true),
		generalServiceTemplate(),
	],
	[USER_SEGMENTATION_TEAM_TYPE_OPERATIONS]: [
		customerServiceTemplate(),
		incidentManagementTemplate(true),
		serviceRequestTemplate(),
	],
	[USER_SEGMENTATION_TEAM_TYPE_HR]: [
		serviceRequestTemplate(),
		hrServiceTemplate(true),
		generalServiceTemplate(),
	],
	[USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE]: [
		serviceRequestTemplate(),
		customerServiceTemplate(true),
		generalServiceTemplate(),
	],
	[USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL]: [
		serviceRequestTemplate(),
		generalServiceTemplate(true),
		customerServiceTemplate(),
	],
	[USER_SEGMENTATION_TEAM_TYPE_SOFTWARE]: [
		customerServiceTemplate(),
		developmentRequestTemplate(true),
		serviceRequestTemplate(),
	],
	[USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT]: [
		customerServiceTemplate(),
		developmentRequestTemplate(),
		generalServiceTemplate(true),
	],
	[USER_SEGMENTATION_TEAM_TYPE_DESIGN]: [
		marketingServiceTemplate(),
		designServiceTemplate(true),
		generalServiceTemplate(),
	],
	[USER_SEGMENTATION_TEAM_TYPE_MARKETING]: [
		customerServiceTemplate(),
		marketingServiceTemplate(true),
		generalServiceTemplate(),
	],
	[USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE]: [
		serviceRequestTemplate(),
		analyticsServiceTemplate(true),
		generalServiceTemplate(),
	],
	[USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT]: [
		serviceRequestTemplate(),
		generalServiceTemplate(true),
		customerServiceTemplate(),
	],
	[USER_SEGMENTATION_TEAM_TYPE_SALES]: [
		customerServiceTemplate(),
		salesServiceTemplate(true),
		generalServiceTemplate(),
	],
	[USER_SEGMENTATION_TEAM_TYPE_FINANCE]: [
		legalServiceTemplate(),
		financeServiceTemplate(true),
		generalServiceTemplate(),
	],
	[USER_SEGMENTATION_TEAM_TYPE_LEGAL]: [
		financeServiceTemplate(),
		legalServiceTemplate(true),
		generalServiceTemplate(),
	],
	[USER_SEGMENTATION_TEAM_TYPE_SKIP]: [
		generalServiceTemplate(true),
		customerServiceTemplate(),
		serviceRequestTemplate(),
	],
};

export const getJsmRecommendedTemplateId = (
	teamType: UserSegmentationTeamTypes | string | undefined,
): string | undefined => {
	if (!teamType) {
		return undefined;
	}

	return JSM_USE_CASE_TEMPLATE_OPTIONS_BY_TEAM_TYPE[teamType as UserSegmentationTeamTypes]?.find(
		({ isRecommended }) => isRecommended,
	)?.templateId;
};
